import { Auth0Context, type Auth0ContextInterface } from '@auth0/auth0-react';
import {
  EAuthProviderType,
  type IAuthAdapter,
  type IAuthAdapterProviderProps,
} from './types';

/**
 * Key reported by the null adapter. Deliberately not a usable provider key, so
 * it can never collide with a configured `authProviders` entry.
 */
export const NULL_PROVIDER_KEY = 'none';

const noop = () => Promise.resolve();

const noResult = () => Promise.resolve(undefined);

/**
 * Token acquisition rejects rather than resolving: a caller that got a
 * non-token back would send it as a bearer credential.
 *
 * `login_required` is the OIDC standard code for "no usable session", and the
 * token manager's matching keys off it — without it this rejection reads as
 * transient and every request burns the full retry backoff before reporting
 * `retries-exhausted` to Sentry.
 */
const rejectNoProvider = () =>
  Promise.reject(
    new Error('login_required: no identity provider is configured'),
  );

/**
 * `useAuth` still reads Auth0's context, so the settled logged-out shape has to
 * be expressed in it. `isLoading: false` is what makes `withProtectedRoute`
 * redirect to login rather than park on a loader forever, and the interactive
 * methods resolve as no-ops because there is nothing to redirect to.
 */
const LOGGED_OUT_CONTEXT: Auth0ContextInterface = {
  error: undefined,
  isAuthenticated: false,
  isLoading: false,
  user: undefined,
  getAccessTokenSilently: rejectNoProvider,
  getAccessTokenWithPopup: noResult,
  getIdTokenClaims: noResult,
  loginWithRedirect: noop,
  loginWithPopup: noop,
  logout: noop,
  handleRedirectCallback: () => Promise.resolve({}),
};

/**
 * Mounts a settled, logged-out session for the case where no provider has been
 * resolved.
 *
 * @param {IAuthAdapterProviderProps} props - Component props
 * @param {ReactNode} props.children - Subtree that consumes the session
 * @returns {JSX.Element} A logged-out auth context
 */
const NullAuthProvider = ({ children }: IAuthAdapterProviderProps) => (
  <Auth0Context value={LOGGED_OUT_CONTEXT}>{children}</Auth0Context>
);

/**
 * Builds the adapter used when no identity provider has been resolved.
 *
 * It loads no SDK and starts no login. The app behaves exactly as it does for a
 * logged-out user, which is what lets `ProtectedRoute` stay untouched.
 *
 * @returns {IAuthAdapter} Adapter with no identity provider behind it
 */
export const createNullAuthAdapter = (): IAuthAdapter => ({
  key: NULL_PROVIDER_KEY,
  type: EAuthProviderType.NONE,
  Provider: NullAuthProvider,
  validateConfig: () => false,
});
